export interface CursorSelection {
  start: number;
  end: number;
}

export interface UpdateCursorOptions<T, TSource> {
  /**
   * The value transform itself
   */
  valueTransform: ValueTransform<T, TSource>;
  /**
   * Type of interaction with the input element
   * @see https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes
   */
  inputType: InputEvent['inputType'] | 'none';
  /**
   * The current display value
   */
  displayValue: string;
  /**
   * The data that was inserted
   */
  data: string | null;
  /**
   * Information on the current cursor selection
   */
  current: {
    cursor: CursorSelection | null;
    /**
     * The new value
     */
    value: TSource;
  };
  /**
   * Information on the previous cursor selection
   */
  previous?: {
    cursor: CursorSelection | null;
    /**
     * The previous value
     */
    value: TSource;
  };
}

type UpdateCursorFn<T, TSource> = (
  selection: UpdateCursorOptions<T, TSource>,
) => CursorSelection | null;

/**
 * A `ValueTransform` provides an interface for transforming a
 * `value` to a `displayValue` and vice versa.
 *
 * - `value` is the actual value that the control should hold
 * - `displayValue` is how that value is represented in the UI
 *
 *
 *
 * @example
 * // Transformer that replaces every space with hyphen for the `value`
 * // and every hyphen with a space for the `displayValue`.
 * const slugTransform = {
 *    transform: value => value.replace(/[\-]/g, ' ')
 *    reverse: displayValue => displayValue.replace(/[ ]/g, '-')
 * };
 */
export interface ValueTransform<
  T = string,
  TSource = string | null | undefined,
> {
  /**
   * This function is called with the `value`. It expects the `displayValue` as a result,
   * meaning that you can control the UI representation for `value`.
   *
   * @param value The value that the control holds
   *
   * @returns
   * The value that the UI should display (aka `displayValue`).
   */
  transform: (value: TSource) => T;
  /**
   * If this function is provided, it will be called when a user changes the value via
   * the UI (e.g. when text is inserted/added).
   * It is called with the `displayValue` and expects the `value` as a result.
   *
   * If you don't need any transformation after changes via the UI, you can
   * omit this function
   *
   * @param displayValue The value that is displayed in the UI
   *
   * @returns
   * The value that the control should hold (aka `value`).
   */
  reverse?: (displayValue: T) => TSource;
  /**
   * If this function is provided, it will be called whenever the position of the cursor
   * changes (e.g. on mouseup, keyup, touchend or when the input value changes).
   * You can use it to change the current cursor selection.
   * You will most probably need it, if your transformation removes or adds characters.
   *
   * @param selection Information on the cursor selection and on changed data
   *
   * @returns
   * The new cursor selection that should be applied to the input.
   */
  updateCursor?: UpdateCursorFn<T, TSource>;
}

/**
 * A transform that preserves both, `value` and `displayValue`.
 */
export const noopTransform: ValueTransform<unknown, unknown> = {
  transform: (v) => v,
};

/**
 * A transform that makes the `displayValue` upper case while
 * preserving the actual `value`.
 */
export const uppercaseTransform: ValueTransform<
  string,
  string | null | undefined
> = {
  transform: (v) => v?.toUpperCase() ?? '',
};

/**
 * A transform that makes the `displayValue` lower case while
 * preserving the actual `value`.
 */
export const lowercaseTransform: ValueTransform<
  string,
  string | null | undefined
> = {
  transform: (v) => v?.toLowerCase() ?? '',
};

export const CursorPositionUpdateFactory = {
  /**
   * @param separatorCount Count of separators inserted after each group
   * @param groupLength Length of a group
   *
   * @returns
   * Utillity function for updating the cursor position for an input
   * that takes group of fixed length followed by separators of fixed
   * length.
   */
  fixedGroups(
    separatorCount: number,
    groupLength: number,
  ): UpdateCursorFn<unknown, string> {
    const separatorsOnDisplayValuePosition = (pos: number) =>
      Math.floor(pos / (groupLength + separatorCount)) * separatorCount;

    const separatorsOnValuePos = (pos: number) =>
      Math.floor(pos / groupLength) * separatorCount;

    return (options) => {
      const {
        inputType,
        current: { cursor },
      } = options;

      // Early exit for tracking calls
      if (inputType === 'none' || inputType.startsWith('delete')) {
        return cursor;
      }

      // data was inserted or deleted
      const {
        current: { value },
        previous,
      } = options;

      // Use end position of previous cursor selection as pivot point.
      const pivot = previous?.cursor?.end ?? 0;
      const previousValue = previous?.value ?? '';

      // By how much did the actual value change
      const valueDiff = value.length - previousValue.length;
      // Project the pivot position in `value` space
      const valuePivot = pivot - separatorsOnDisplayValuePosition(pivot);
      // Number of separators inserted for new value
      const insertedCount = separatorsOnValuePos(valuePivot + valueDiff);
      // final position from pivot, data inserted and separators inserted
      const finalPosition = valuePivot + valueDiff + insertedCount;

      return {
        start: finalPosition,
        end: finalPosition,
      };
    };
  },
};
